import React from "react";
import { motion } from "framer-motion";
import { BadgeDollarSign, TrendingUp, Lock, CheckCircle2 } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "../../lib/animations";

const GOLD = "#C9A84C";
const GOLD_BRIGHT = "#E8C878";
const PURPLE = "#8B5CD6";
const CREAM = "#F0EBE1";
const TEXT_PRIMARY = "rgba(255, 255, 255, 0.95)";
const TEXT_SECONDARY = "rgba(255, 255, 255, 0.7)";
const TEXT_MUTED = "rgba(255, 255, 255, 0.5)";

const FEATURES = [
  {
    Icon: BadgeDollarSign,
    title: "Zero Platform Cost.",
    body: "No monthly fee, no setup charge, no minimum term. Entirely free to operate.",
  },
  {
    Icon: TrendingUp,
    title: "Performance Fee Only.",
    body: "We charge a percentage of recovered revenue — nothing else. Our incentives are identical to yours.",
  },
  {
    Icon: Lock,
    title: "Guaranteed Rate.",
    body: "Your performance fee rate is fixed and will never increase once you begin. Transparent, predictable, permanent.",
  },
];

const TERMS = [
  { label: "Setup fee", value: "R0.00", valueStyle: {} },
  { label: "Monthly fee", value: "R0.00", valueStyle: {} },
  { label: "Performance fee", value: "On recovery only", valueStyle: { color: "var(--gold-mid)" } },
  { label: "Contract term", value: "None", valueStyle: {} },
  { label: "Integration support", value: "Included", valueStyle: { color: "#10B981" } },
];

export default function Offer() {
  return (
    <>
      <style>{`
        @keyframes offerShimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes offerPulseRing {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.7); opacity: 0; }
        }
        @keyframes offerFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes offerGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(201, 168, 76, 0.3); }
          50% { box-shadow: 0 0 30px rgba(201, 168, 76, 0.5); }
        }

        .modern-offer-section {
          background: linear-gradient(180deg, #0A080F 0%, #07050E 100%);
          padding: 100px 24px 120px;
          position: relative;
          overflow: hidden;
        }

        /* Tablet Responsive */
        @media (max-width: 1023px) {
          .modern-offer-section {
            padding: 60px 20px 80px;
          }
          
          .offer-grid {
            gap: 40px;
          }
          
          .offer-grid .offer-card-col {
            order: -1;
          }
          
          .offer-headline {
            font-size: clamp(32px, 4vw, 48px);
          }
          
          .offer-subtext {
            font-size: 17px;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 639px) {
          .modern-offer-section {
            padding: 40px 16px 60px;
          }
          
          .offer-grid {
            gap: 32px;
          }
          
          .offer-headline {
            font-size: clamp(28px, 6vw, 40px);
          }
          
          .offer-subtext {
            font-size: 15px;
          }
          
          .offer-divider {
            margin: 24px 0;
          }
        }

        .modern-offer-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(ellipse at 25% 25%, rgba(184, 146, 42, 0.08) 0%, transparent 40%),
            radial-gradient(ellipse at 75% 75%, rgba(139, 92, 214, 0.06) 0%, transparent 50%);
          pointer-events: none;
          z-index: 0;
        }

        .offer-grid-background {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(184, 146, 42, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(184, 146, 42, 0.03) 1px, transparent 1px);
          background-size: 80px 80px;
          pointer-events: none;
          z-index: 1;
        }

        .offer-content {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          margin: 0 auto;
        }

        .offer-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 60px;
          align-items: start;
        }

        @media (min-width: 1024px) {
          .offer-grid {
            grid-template-columns: 58% 42%;
            gap: 80px;
          }
        }

        .section-label {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .section-rule {
          width: 80px;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            ${GOLD} 50%,
            transparent 100%
          );
        }

        .section-label-text {
          font-family: 'Syne', system-ui, -apple-system, sans-serif;
          font-weight: 600;
          font-size: 11px;
          color: ${GOLD};
          text-transform: uppercase;
          letter-spacing: 0.14em;
        }

        .offer-headline {
          font-family: 'Cabinet Grotesk', 'Outfit', sans-serif;
          font-weight: 800;
          font-size: clamp(42px, 5vw, 64px);
          color: ${TEXT_PRIMARY};
          letter-spacing: -0.03em;
          line-height: 1.0;
          margin: 0 0 24px;
        }

        .offer-subtext {
          font-family: 'Outfit', sans-serif;
          font-weight: 400;
          font-size: 18px;
          color: ${TEXT_SECONDARY};
          line-height: 1.6;
          max-width: 500px;
          margin: 0 0 32px;
        }

        .offer-divider {
          height: 1px;
          background: linear-gradient(
            90deg,
            ${GOLD} 0%,
            transparent 50%,
            ${GOLD} 100%
          );
          margin: 32px 0;
          max-width: 200px;
        }

        .feature-item {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          padding: 20px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .feature-item:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(201, 168, 76, 0.3);
          transform: translateY(-2px);
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.15), rgba(201, 168, 76, 0.08));
          border: 1px solid rgba(201, 168, 76, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .feature-item:hover .feature-icon {
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.25), rgba(201, 168, 76, 0.15));
          border-color: rgba(201, 168, 76, 0.4);
          transform: scale(1.05);
        }

        .feature-content h4 {
          font-family: 'Cabinet Grotesk', 'Outfit', sans-serif;
          font-weight: 600;
          font-size: 18px;
          color: ${TEXT_PRIMARY};
          letter-spacing: -0.01em;
          margin: 0 0 8px;
        }

        .feature-content p {
          font-family: 'Outfit', sans-serif;
          font-weight: 400;
          font-size: 15px;
          color: ${TEXT_SECONDARY};
          line-height: 1.6;
          margin: 0;
        }

        .cta-section {
          margin-top: 40px;
        }

        .primary-cta {
          background: linear-gradient(135deg, ${GOLD}, ${GOLD_BRIGHT});
          color: #07050E;
          font-family: 'Syne', system-ui, -apple-system, sans-serif;
          font-weight: 600;
          font-size: 16px;
          letter-spacing: 0.02em;
          padding: 16px 32px;
          border: none;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 8px 24px rgba(201, 168, 76, 0.3);
          display: inline-flex;
          align-items: center;
          gap: 12px;
        }

        .primary-cta:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(201, 168, 76, 0.4);
          background: linear-gradient(135deg, ${GOLD_BRIGHT}, ${GOLD});
        }

        .trust-badges {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 16px;
          opacity: 0.8;
        }

        .trust-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'Outfit', sans-serif;
          font-size: 13px;
          color: ${TEXT_MUTED};
        }

        .trust-badge svg {
          width: 16px;
          height: 16px;
          color: ${GOLD};
        }

        /* Feature Items Mobile Optimization */
        @media (max-width: 639px) {
          .feature-item {
            padding: 12px;
            gap: 12px;
          }
          
          .feature-icon {
            width: 32px;
            height: 32px;
          }
          
          .feature-content h4 {
            font-size: 16px;
          }
          
          .feature-content p {
            font-size: 14px;
          }
        }

        /* CTA Section Mobile Optimization */
        @media (max-width: 639px) {
          .cta-section {
            margin-top: 32px;
          }
          
          .primary-cta {
            width: 100%;
            justify-content: center;
            padding: 14px 20px;
            font-size: 15px;
          }
          
          .trust-badges {
            flex-direction: column;
            gap: 8px;
            align-items: flex-start;
          }
          
          .trust-badge {
            font-size: 12px;
          }
        }
      `}</style>

      <section id="offer" className="modern-offer-section" data-testid="offer-section">
        {/* Grid background */}
        <div className="offer-grid-background" aria-hidden="true" />
        
        <div className="offer-content">
          <div className="offer-grid">
            {/* ── LEFT COLUMN — MODERN HEADLINE + FEATURES ── */}
            <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            {/* Label */}
            <motion.div variants={fadeUp}>
              <div className="section-label">
                <span className="section-rule" aria-hidden="true" />
                <span className="section-label-text">THE INVESTMENT</span>
                <span className="section-rule" aria-hidden="true" />
              </div>
            </motion.div>

            {/* Modern Headline */}
            <motion.h2
              variants={fadeUp}
              className="offer-headline"
            >
              We only invoice{" "}
              <span
                className="italic"
                style={{
                  backgroundImage: "linear-gradient(90deg, #B8922A 0%, #E8C878 50%, #FFFFFF 100%)",
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  color: "transparent",
                  animation: "offerShimmer 4s ease-in-out infinite",
                }}
              >
                when we win.
              </span>
            </motion.h2>

            {/* Enhanced Subtext */}
            <motion.p
              variants={fadeUp}
              className="offer-subtext"
            >
              No monthly fee. No setup cost. No contract. A single performance fee on revenue we recover — nothing more.
            </motion.p>

            {/* Modern Divider */}
            <motion.div variants={fadeUp} className="offer-divider" />

            {/* Enhanced Feature Blocks */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {FEATURES.map(({ Icon, title, body }, index) => (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  custom={index}
                  className="feature-item"
                >
                  <div className="feature-icon">
                    <Icon size={20} style={{ color: GOLD }} aria-hidden="true" />
                  </div>
                  <div className="feature-content">
                    <h4>{title}</h4>
                    <p>{body}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Conversion-Optimized CTA Section */}
            <motion.div variants={fadeUp} className="cta-section">
              <button
                className="primary-cta"
                onClick={() => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="offer-cta"
              >
                Start Recovering Revenue Now
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
              
              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="trust-badge">
                  <CheckCircle2 />
                  <span>No setup fees</span>
                </div>
                <div className="trust-badge">
                  <CheckCircle2 />
                  <span>Free integration</span>
                </div>
                <div className="trust-badge">
                  <CheckCircle2 />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* ── RIGHT COLUMN — ENHANCED PURPLE GRADIENT CARD ── */}
          <motion.div
            className="offer-card-col"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            <div
              data-testid="offer-card"
              style={{
                background:
                  "radial-gradient(70% 60% at 18% 20%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 62%), radial-gradient(60% 55% at 72% 30%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #1A1060 0%, #130A48 100%)",
                border: "1px solid rgba(184,146,42,0.18)",
                borderRadius: 24,
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(26, 16, 96, 0.3), 0 8px 16px rgba(0, 0, 0, 0.2)",
                transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
                "--text-primary": "rgba(255,255,255,0.92)",
                "--text-secondary": "rgba(255,255,255,0.76)",
                "--text-muted": "rgba(255,255,255,0.58)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 25px 50px rgba(26, 16, 96, 0.4), 0 12px 24px rgba(0, 0, 0, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(26, 16, 96, 0.3), 0 8px 16px rgba(0, 0, 0, 0.2)";
              }}
            >
              {/* Zone 1 — Display */}
              <div
                className="offer-zone1"
                style={{ padding: "28px 28px 20px" }}
              >
                {/* Top row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <span
                    style={{
                      fontFamily: "'Fira Code', monospace",
                      fontWeight: 600,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.10em",
                      color: "var(--text-muted)",
                    }}
                  >
                    Client Pricing Terms
                  </span>
                  <span
                    style={{
                      background: "rgba(184,146,42,0.10)",
                      border: "1px solid rgba(184,146,42,0.28)",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontFamily: "'Fira Code', monospace",
                      fontWeight: 600,
                      fontSize: 11,
                      color: "var(--gold-primary)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    PERFORMANCE ONLY
                  </span>
                </div>

                {/* 0% display */}
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px 0 16px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <div
                    className="offer-zero text-gold-gradient"
                    style={{
                      fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif",
                      fontWeight: 800,
                      fontSize: 72,
                      letterSpacing: "-0.04em",
                      lineHeight: 1.0,
                    }}
                  >
                    0%
                  </div>
                  <p
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 400,
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      marginTop: 4,
                      marginBottom: 0,
                    }}
                  >
                    monthly platform fee. Always.
                  </p>
                </div>

                {/* Performance fee visual */}
                <div
                  style={{
                    marginTop: 14,
                    background: "rgba(184,146,42,0.05)",
                    border: "1px solid rgba(184,146,42,0.14)",
                    borderRadius: 12,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div>
                    <span
                      style={{
                        display: "block",
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 400,
                        fontSize: 14,
                        color: "var(--text-secondary)",
                        lineHeight: 1.5,
                      }}
                    >
                      Performance fee
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontFamily: "'Fira Code', monospace",
                        fontWeight: 500,
                        fontSize: 12,
                        color: "var(--text-muted)",
                      }}
                    >
                      charged on recovered revenue only
                    </span>
                  </div>
                  <span
                    className="text-gold-gradient"
                    style={{
                      fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif",
                      fontWeight: 700,
                      fontSize: 28,
                      flexShrink: 0,
                    }}
                  >
                    10%
                  </span>
                </div>
                <p
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 12,
                    color: "var(--text-muted)",
                    marginTop: 10,
                    marginBottom: 0,
                    textAlign: "right",
                  }}
                >
                  On R50,000 recovered → R5,000 fee. You net R45,000.
                </p>
              </div>

              {/* Zone 2 — Term sheet */}
              <div
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                  padding: "20px 28px 24px",
                  background:
                    "radial-gradient(70% 60% at 18% 20%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 62%), radial-gradient(60% 55% at 72% 30%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #1A1060 0%, #130A48 100%)",
                }}
              >
                {TERMS.map((row, idx) => (
                  <div
                    key={row.label}
                    className="offer-term-row"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 0",
                      borderBottom: idx === TERMS.length - 1 ? "none" : "1px solid rgba(255,255,255,0.05)",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: 14,
                        color: "var(--text-muted)",
                      }}
                    >
                      {row.label}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Fira Code', monospace",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--text-primary)",
                        ...row.valueStyle,
                      }}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}

                {/* Term footer */}
                <div
                  style={{
                    marginTop: 14,
                    background: "rgba(184,146,42,0.05)",
                    border: "1px solid rgba(184,146,42,0.15)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    className="shrink-0"
                    aria-hidden="true"
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "var(--gold-primary)",
                      boxShadow: "0 0 8px rgba(184,146,42,0.7)",
                      animation: "offerPulseRing 2s ease-in-out infinite",
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 400,
                      fontSize: 13,
                      color: "var(--text-secondary)",
                    }}
                  >
                    Transparent Pricing and intregration support included
                  </span>
                </div>

                {/* Ghost button */}
                <button
                  type="button"
                  style={{
                    width: "100%",
                    marginTop: 14,
                    background: "transparent",
                    border: "1px solid rgba(184,146,42,0.22)",
                    borderRadius: 12,
                    padding: "13px 20px",
                    fontFamily: "'Fira Code', monospace",
                    fontSize: 13,
                    color: "var(--gold-mid)",
                    cursor: "pointer",
                    letterSpacing: "0.03em",
                    transition: "all 0.2s cubic-bezier(0.22,1,0.36,1)",
                    textAlign: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(184,146,42,0.06)";
                    e.currentTarget.style.borderColor = "rgba(184,146,42,0.40)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "rgba(184,146,42,0.22)";
                  }}
                >
                  View Full Terms ↗
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        </div>
      </section>
    </>
  );
}
